export type GroupScore = {
  correct: number;
  total: number;
  errors: number;
};

export type ScreeningStatusKey =
  | "colorVisionSummaryScreeningStatusInsufficientData"
  | "colorVisionSummaryScreeningStatusNormal"
  | "colorVisionSummaryScreeningStatusLikelyDeficiency"
  | "colorVisionSummaryScreeningStatusBorderline";

export type TypeHintKey =
  | "colorVisionSummaryTypeHintLikelyDeficiency"
  | "colorVisionSummaryTypeHintNoStrongPattern";

export type ColorVisionMetrics = {
  screening: GroupScore;
  transformation: GroupScore;
  vanishing: GroupScore;
  hidden: GroupScore;
  typing: GroupScore;
  screeningStatusKey: ScreeningStatusKey;
  confidencePercent: number;
  screeningClass: "normal" | "deficiency" | "borderline" | "insufficient";
  typeHintKey: TypeHintKey;
};

function countGroup(results: boolean[], startPlate: number, endPlate: number): GroupScore {
  const startIndex = startPlate - 1;
  const endIndex = Math.min(endPlate - 1, results.length - 1);

  if (endIndex < startIndex) {
    return { correct: 0, total: 0, errors: 0 };
  }

  let correct = 0;
  for (let i = startIndex; i <= endIndex; i += 1) {
    if (results[i] === true) {
      correct += 1;
    }
  }

  const total = endIndex - startIndex + 1;
  return { correct, total, errors: total - correct };
}

function countGroupByPlates(results: boolean[], plateNumbers: number[]): GroupScore {
  const administered = plateNumbers.filter((plate) => plate - 1 < results.length);

  let correct = 0;
  for (const plate of administered) {
    if (results[plate - 1] === true) {
      correct += 1;
    }
  }

  const total = administered.length;
  return { correct, total, errors: total - correct };
}

const TYPING_CLUES_PLATES = [22, 23, 24, 26];

// The only screening totals a fully-administered run can produce: 17 for the
// Small plate-count configuration, 21 for Medium/Large (their first 21 plates).
// Any other total means the run was stopped early and shouldn't be scored as complete.
const COMPLETE_SCREENING_TOTALS = [17, 21];

export function buildColorVisionMetrics(results: boolean[]): ColorVisionMetrics {
  const screening = countGroup(results, 1, 21);
  const transformation = countGroup(results, 2, 9);
  const vanishing = countGroup(results, 10, 17);
  const hidden = countGroup(results, 18, 21);
  const typing = countGroupByPlates(results, TYPING_CLUES_PLATES);

  const screeningComplete = COMPLETE_SCREENING_TOTALS.includes(screening.total);
  const correctOn21Scale = screeningComplete ? (screening.correct / screening.total) * 21 : 0;

  let screeningStatusKey: ScreeningStatusKey = "colorVisionSummaryScreeningStatusInsufficientData";
  let screeningClass: ColorVisionMetrics["screeningClass"] = "insufficient";
  if (screeningComplete) {
    if (correctOn21Scale >= 17) {
      screeningStatusKey = "colorVisionSummaryScreeningStatusNormal";
      screeningClass = "normal";
    } else if (correctOn21Scale <= 13) {
      screeningStatusKey = "colorVisionSummaryScreeningStatusLikelyDeficiency";
      screeningClass = "deficiency";
    } else {
      screeningStatusKey = "colorVisionSummaryScreeningStatusBorderline";
      screeningClass = "borderline";
    }
  }

  let confidencePercent = 0;
  if (screeningComplete) {
    if (correctOn21Scale > 16) {
      confidencePercent = Math.min(100, Math.round(((correctOn21Scale - 16) / 5) * 100));
    } else if (correctOn21Scale < 14) {
      confidencePercent = Math.min(100, Math.round(((14 - correctOn21Scale) / 5) * 100));
    } else {
      const distFromMidpoint = Math.abs(15 - correctOn21Scale);
      confidencePercent = Math.round((1 - distFromMidpoint / 2) * 100);
    }
  }

  const typeHintKey: TypeHintKey =
    screeningComplete && correctOn21Scale <= 13
      ? "colorVisionSummaryTypeHintLikelyDeficiency"
      : "colorVisionSummaryTypeHintNoStrongPattern";

  return {
    screening,
    transformation,
    vanishing,
    hidden,
    typing,
    screeningStatusKey,
    confidencePercent,
    screeningClass,
    typeHintKey,
  };
}
